import { type ActionEvent, Controller } from '@hotwired/stimulus';

/**
 * Copies text or an image to the clipboard and shows a "Copied" state on the
 * control that was pressed.
 *
 * What to copy comes from, in order of preference:
 *   1. the action's own param:  data-clipboard-text-param="..."
 *   2. a read-only field:       data-clipboard-target="input"
 *   3. the text value:          data-clipboard-text-value="..."
 *   4. a PNG image:             data-clipboard-image-url-value="..."
 *
 * Feedback appears on the `button` target if there is one, otherwise on the
 * element that triggered the action. If that control holds more than text
 * (an icon, say), wrap its text in a `label` target so only the text changes.
 *
 *   <button data-controller="clipboard" data-action="clipboard#copy"
 *           data-clipboard-text-param="https://example.com/x">Copy link</button>
 *
 * Requires the async Clipboard API (a secure context); a failed write shows
 * no "Copied" state.
 */
export default class ClipboardController extends Controller {
  declare private readonly inputTarget: HTMLInputElement;
  declare private readonly hasInputTarget: boolean;
  declare private readonly buttonTarget: HTMLElement;
  declare private readonly hasButtonTarget: boolean;
  declare private readonly labelTarget: HTMLElement;
  declare private readonly hasLabelTarget: boolean;
  declare private readonly textValue: string;
  declare private readonly imageUrlValue: string;
  declare private readonly copiedLabelValue: string;

  public static readonly targets = ['input', 'button', 'label'];
  public static readonly values = {
    text: String,
    imageUrl: String,
    copiedLabel: { type: String, default: 'Copied' }
  };

  private resetTimeout: number | undefined;

  public disconnect(): void {
    window.clearTimeout(this.resetTimeout);
  }

  public async copy(event: ActionEvent): Promise<void> {
    const text = this._textToCopy(event);

    if (!text && !this.imageUrlValue) {
      return;
    }

    // currentTarget is cleared once the handler yields, so take it first
    const control = this.hasButtonTarget
      ? this.buttonTarget
      : (event.currentTarget as HTMLElement);

    if (text) {
      await navigator.clipboard.writeText(text);
    } else {
      await this._writeImage();
    }

    this._showCopied(control);
  }

  private _textToCopy(event: ActionEvent): string {
    const param = event.params.text as string | undefined;

    if (param) {
      return param;
    }

    return this.hasInputTarget ? this.inputTarget.value : this.textValue;
  }

  private async _writeImage(): Promise<void> {
    // The blob is passed as a promise so the write starts inside the user
    // gesture; Safari rejects it if the fetch is awaited first
    const blob = fetch(this.imageUrlValue, {
      credentials: 'same-origin'
    }).then(async response => {
      if (!response.ok) {
        throw new Error(
          `Failed to fetch image: ${response.status} ${response.statusText}`
        );
      }

      return response.blob();
    });

    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  }

  private _showCopied(control: HTMLElement): void {
    const label = this.hasLabelTarget ? this.labelTarget : control;
    const original = label.dataset.originalLabel ?? label.textContent;

    label.dataset.originalLabel = original;
    label.textContent = this.copiedLabelValue;
    control.classList.add('copied');

    window.clearTimeout(this.resetTimeout);
    this.resetTimeout = window.setTimeout(() => {
      label.textContent = original;
      control.classList.remove('copied');
    }, 2000);
  }
}
